import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import {
  importBankStatementExcel,
  previewBankStatementExcel,
} from "../api/bankStatementExcelApi";

const { Dragger } = Upload;
const { Title, Text } = Typography;

export default function UploadBankStatementExcel({
  selectedBank,
  selectedAccountNumber,
  selectedCurrency,
  onImportSuccess,
  extraAction,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [headersValid, setHeadersValid] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [confirmedPreview, setConfirmedPreview] = useState(false);

  const resetAll = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setPreviewError("");
    setHeadersValid(false);
    setPreviewModalOpen(false);
    setConfirmedPreview(false);
  };

  const parsedCurrencies = useMemo(() => {
    return [
      ...new Set(
        previewData
          .map((item) => item?.currency)
          .filter(
            (value) => value !== undefined && value !== null && value !== ""
          )
      ),
    ];
  }, [previewData]);

  const previewColumns = useMemo(
    () => [
      {
        title: "№",
        dataIndex: "rowNumber",
        key: "rowNumber",
        width: 70,
        fixed: "left",
      },
      {
        title: "Огноо",
        dataIndex: "transactionDate",
        key: "transactionDate",
        width: 180,
      },
      {
        title: "Валют",
        dataIndex: "currency",
        key: "currency",
        width: 110,
        render: (value) =>
          value ? <Tag color="blue">{value}</Tag> : <Tag color="default">-</Tag>,
      },
      {
        title: "Зарлага",
        dataIndex: "expense",
        key: "expense",
        width: 140,
        render: (value) => value ?? "-",
      },
      {
        title: "Орлого",
        dataIndex: "income",
        key: "income",
        width: 140,
        render: (value) => value ?? "-",
      },
      {
        title: "Ханш",
        dataIndex: "exchangeRate",
        key: "exchangeRate",
        width: 120,
        render: (value) => value ?? "-",
      },
      {
        title: "Харилцагч",
        dataIndex: "counterpartyName",
        key: "counterpartyName",
        width: 220,
        render: (value) => value || "-",
      },
      {
        title: "Харилцагчийн данс",
        dataIndex: "counterpartyAccount",
        key: "counterpartyAccount",
        width: 220,
        render: (value) => value || "-",
      },
      {
        title: "Үлдэгдэл",
        dataIndex: "balance",
        key: "balance",
        width: 140,
        render: (value) => value ?? "-",
      },
      {
        title: "Гүйлгээний утга",
        dataIndex: "transactionDescription",
        key: "transactionDescription",
        width: 320,
        render: (value) => value || "-",
      },
    ],
    []
  );

const handlePreviewFile = async (file) => {
  if (!selectedBank) {
    message.error("Банк тодорхойгүй байна");
    return;
  }

  if (!selectedAccountNumber) {
    message.error("Дансны дугаар тодорхойгүй байна");
    return;
  }

  setLoadingPreview(true);
  setPreviewError("");
  setPreviewData([]);
  setHeadersValid(false);
  setConfirmedPreview(false);

  try {
    const data = await previewBankStatementExcel(
      file,
      selectedBank,
      selectedAccountNumber
    );

    const rows = Array.isArray(data?.rows)
      ? data.rows.map((item, index) => ({
          ...item,
          currency: item?.currency ?? selectedCurrency ?? null,
          key: `${item?.rowNumber ?? index}-${index}`,
        }))
      : [];

    setSelectedFile(file);
    setPreviewData(rows);
    setHeadersValid(Boolean(data?.valid));
    setPreviewError(
      data?.valid ? "" : data?.error || "Excel validation failed"
    );
    setPreviewModalOpen(true);
  } catch (error) {
    setSelectedFile(file);
    setPreviewData([]);
    setHeadersValid(false);
    setPreviewError(error?.message || "Preview failed");
    setPreviewModalOpen(true);
  } finally {
    setLoadingPreview(false);
  }
};

  const handleApply = async () => {
    if (!selectedBank) {
      message.error("Банк тодорхойгүй байна");
      return;
    }

    if (!selectedAccountNumber) {
      message.error("Дансны дугаар тодорхойгүй байна");
      return;
    }

    if (!selectedFile) {
      message.error("Excel файл сонгоно уу");
      return;
    }

    if (!headersValid) {
      message.error("Excel format буруу байна");
      return;
    }

    if (previewData.length === 0) {
      message.error("Parsed rows байхгүй байна");
      return;
    }

    if (!confirmedPreview) {
      message.error("Parsed rows-ийг шалгаснаа баталгаажуулна уу");
      return;
    }

    setLoadingImport(true);
    setPreviewError("");

    try {
      const data = await importBankStatementExcel(
        selectedFile,
        selectedBank,
        selectedAccountNumber
      );

      if (data?.success) {
        message.success(
          `Imported ${data?.savedCount ?? 0} rows, skipped ${
            data?.skippedCount ?? 0
          }`
        );
        setPreviewModalOpen(false);
        resetAll();
        onImportSuccess?.(data);
      } else {
        const err = data?.error || "Import failed";
        setPreviewError(err);
        message.error(err);
      }
    } catch (error) {
      const err = error?.message || "Import failed";
      setPreviewError(err);
      message.error(err);
    } finally {
      setLoadingImport(false);
    }
  };

  const uploadProps = {
    multiple: false,
    accept: ".xlsx,.xls",
    showUploadList: false,
    disabled: !selectedBank,
    beforeUpload: (file) => {
      handlePreviewFile(file);
      return false;
    },
  };

  return (
    <>
      <Card>
        <div className="w-full flex flex-col gap-4">
          <Title level={4} className="!mb-0">
            Excel файл оруулах
          </Title>

          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Excel файлаа энд чирж оруулах эсвэл дарж сонгоно уу
            </p>
            <p className="ant-upload-hint">
              Upload хийсний дараа parsed rows preview modal дээр харагдана
            </p>
          </Dragger>

          <Space wrap>
            {selectedFile ? (
              <Tag color="blue">{selectedFile.name}</Tag>
            ) : (
              <Tag>Файл сонгоогүй</Tag>
            )}

            {loadingPreview && <Tag color="processing">Уншиж байна...</Tag>}

            {headersValid ? (
              <Tag color="green">Excel зөв</Tag>
            ) : (
              <Tag color="default">Excel шалгаагүй</Tag>
            )}

            <Tag color={selectedCurrency ? "geekblue" : "default"}>
              Валют: {selectedCurrency || "-"}
            </Tag>

            {selectedFile && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => setPreviewModalOpen(true)}
              >
                Preview
              </Button>
            )}

            {selectedFile && (
              <Button danger icon={<DeleteOutlined />} onClick={resetAll}>
                Clear
              </Button>
            )}

            {extraAction}
          </Space>

          {previewError && !previewModalOpen && (
            <Alert
              type="error"
              showIcon
              message="Excel алдаа"
              description={previewError}
            />
          )}

          <Text type="secondary">
            Файл upload хийсний дараа parsed rows-ийг шалгаад Apply дарна.
          </Text>
        </div>
      </Card>

      <Modal
        title="Parsed rows шалгах"
        open={previewModalOpen}
        onCancel={() => setPreviewModalOpen(false)}
        width={1280}
        destroyOnClose={false}
        footer={
          <Space>
            <Button onClick={() => setPreviewModalOpen(false)}>Хаах</Button>
            <Button danger onClick={resetAll}>
              Файл арилгах
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={loadingImport}
              onClick={handleApply}
              disabled={
                loadingPreview ||
                !headersValid ||
                previewData.length === 0 ||
                !confirmedPreview
              }
            >
              Apply
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {selectedFile && (
            <Alert
              showIcon
              type={headersValid ? "success" : "warning"}
              message={headersValid ? "Parsed rows ready" : "Excel format issue"}
              description={
                <div>
                  <div>
                    <strong>Файл:</strong> {selectedFile.name}
                  </div>
                  <div>
                    <strong>Rows:</strong> {previewData.length}
                  </div>
                  <div>
                    <strong>Bank:</strong> {selectedBank || "-"}
                  </div>
                  <div>
                    <strong>Account:</strong> {selectedAccountNumber || "-"}
                  </div>
                  <div>
                    <strong>Selected currency:</strong>{" "}
                    {selectedCurrency || "-"}
                  </div>
                  <div>
                    <strong>Parsed currencies:</strong>{" "}
                    {parsedCurrencies.length > 0
                      ? parsedCurrencies.join(", ")
                      : "-"}
                  </div>
                </div>
              }
            />
          )}

          {previewError && (
            <Alert
              type="error"
              showIcon
              message="Import error"
              description={previewError}
            />
          )}

          <Checkbox
            checked={confirmedPreview}
            onChange={(e) => setConfirmedPreview(e.target.checked)}
            disabled={!headersValid || previewData.length === 0}
          >
            Би parsed rows-ийг шалгасан, Apply хийж болно
          </Checkbox>

          <Table
            columns={previewColumns}
            dataSource={previewData}
            bordered
            size="small"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1900, y: 500 }}
            locale={{
              emptyText: loadingPreview ? "Уншиж байна..." : "Parsed row олдсонгүй",
            }}
          />
        </Space>
      </Modal>
    </>
  );
}